import { Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { HorizonTrackingService } from '@services/api/horizon-tracking.service';
import { shouldHideError } from '@services/helpers/server-error-silence';
import { ToasterService } from '@services/util/toaster.service';
import { apiErrorBody } from '@services/api/api-error';
import moment from 'moment';
import { MenuItem } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { Subscription } from 'rxjs';
import { horizonActivityTitle, isHorizonPath } from './horizon-activity-text';
import {
  HORIZON_ACTIVITY_FILTERS,
  HORIZON_DATE_RANGES,
  HORIZON_INELIGIBLE_REASONS,
  HORIZON_STAGES,
  HorizonAccess,
  HorizonActivityItem,
  HorizonInsights,
  HorizonLeadOverview,
  HorizonLeadProperty,
  HorizonStage,
  HorizonSummaryMetric,
  hasPeriodComparison,
} from './lead-horizon-tracking.model';

const ACCESS_STATUS_LABELS: Record<string, string> = {
  not_invited: 'Not invited',
  invited: 'Invited',
  active: 'Active',
  suspended: 'Suspended',
};

/** Icons for the backend's activity `kind`, used when a row comes without an icon name. */
const ACTIVITY_KIND_ICONS: Record<string, string> = {
  login: 'fa-solid fa-right-to-bracket',
  logout: 'fa-solid fa-right-from-bracket',
  register: 'fa-solid fa-user-plus',
  password_reset: 'fa-solid fa-key',
  page_view: 'fa-regular fa-file-lines',
  document_view: 'fa-regular fa-eye',
  document_download: 'fa-solid fa-download',
  property_view: 'fa-solid fa-house',
  shortlist: 'fa-regular fa-heart',
  share: 'fa-solid fa-share-nodes',
  information_request: 'fa-regular fa-comment-dots',
  message: 'fa-regular fa-comment-dots',
  booking: 'fa-regular fa-calendar-check',
  callback: 'fa-solid fa-phone',
};

/**
 * A page view carries `label`, the page in words, and is titled "Viewed {label}" (contract §4.2). Older rows
 * without `label` were titled with the buyer's portal path ("Viewed /lead/6ac7…"); those are still turned into
 * words. Either way a row is never a link: `link` is always null and is dropped here, so nothing in the timeline
 * can navigate to a Remmi route.
 */
/** Kinds the agent has to act on; their rows are highlighted. */
const ACTION_KINDS = ['information_request', 'message', 'booking', 'callback'];

function toReadableActivity(item: HorizonActivityItem): HorizonActivityItem {
  // A request's subtitle is what the buyer wrote, so it is kept even when it reads like a portal path.
  const isRequestRow = ACTION_KINDS.includes(item?.kind ?? item?.type ?? '');
  return {
    ...item,
    title: horizonActivityTitle(item),
    subtitle: !isRequestRow && isHorizonPath(item?.subtitle) ? undefined : item?.subtitle,
    link: null,
  };
}

/** Horizon runs on Brisbane time (no daylight saving), so ranges and times are shown at a fixed +10:00. */
const BRISBANE_UTC_OFFSET_MINUTES = 600;

/** Most Viewed Properties shows this many lots, ranked from up to `MOST_VIEWED_SAMPLE` of the buyer's viewed lots. */
const MOST_VIEWED_COUNT = 5;
const MOST_VIEWED_SAMPLE = 50;

const ACCESS_ACTION_SUCCESS = {
  suspend: 'Horizon access suspended.',
  restore: 'Horizon access restored.',
  'cancel-invite': 'Invitation cancelled. The invite link no longer works.',
};

const ACCESS_ACTION_FAILURE = {
  suspend: 'Horizon access could not be suspended.',
  restore: 'Horizon access could not be restored.',
  'cancel-invite': 'The invitation could not be cancelled.',
};

/**
 * LeadHorizonTrackingComponent
 *
 * Horizon access and activity for one lead — what the client has looked at and how engaged they
 * are. Everything is scoped to `leadId`; nothing here reads or creates a Horizon customer record.
 * Property panels are always drawn, with an empty note until the backend has data for them.
 */
@Component({
  selector: 'app-lead-horizon-tracking',
  templateUrl: './lead-horizon-tracking.component.html',
  styleUrls: ['./lead-horizon-tracking.component.scss'],
})
export class LeadHorizonTrackingComponent implements OnChanges, OnDestroy {
  @Input() leadId: string | null = null;

  /** The lead's project, used by the "View Project Report" link. */
  @Input() projectId: string | null = null;

  readonly dateRanges = HORIZON_DATE_RANGES;
  readonly activityFilters = HORIZON_ACTIVITY_FILTERS;
  readonly horizonStages = HORIZON_STAGES;

  /**
   * The tiles QA approved against the design: Properties Viewed and Shortlisted are left out. Icons match
   * Reporting → Horizon Tracking, so the same figure carries the same icon on both screens.
   */
  readonly summaryCards: { key: string; label: string; icon: string; isDuration?: boolean; filter?: string }[] = [
    { key: 'visits', label: 'Horizon Visits', icon: 'fa-solid fa-door-open' },
    { key: 'pages_viewed', label: 'Pages Viewed', icon: 'fa-regular fa-file-lines' },
    { key: 'updates_viewed', label: 'Updates Viewed', icon: 'fa-regular fa-bell' },
    { key: 'documents_viewed', label: 'Documents Viewed', icon: 'fa-regular fa-eye' },
    { key: 'documents_downloaded', label: 'Documents Downloaded', icon: 'fa-solid fa-download' },
    // These two open the timeline below on the matching requests, so the agent can read them.
    { key: 'information_requests', label: 'Information Requests', icon: 'fa-regular fa-comment-dots', filter: 'requests' },
    { key: 'display_suite_bookings', label: 'Display Suite Bookings', icon: 'fa-regular fa-calendar-check', filter: 'bookings' },
    { key: 'time_spent_seconds', label: 'Time Spent', icon: 'fa-regular fa-clock', isDuration: true },
  ];

  private readonly activityIcons: Record<string, string> = {
    eye: 'fa-regular fa-eye',
    download: 'fa-solid fa-download',
    file: 'fa-regular fa-file-lines',
    document: 'fa-regular fa-file-lines',
    message: 'fa-regular fa-comment-dots',
    user: 'fa-regular fa-user',
    bell: 'fa-regular fa-bell',
    calendar: 'fa-regular fa-calendar-check',
    home: 'fa-solid fa-house',
    heart: 'fa-solid fa-heart',
    share: 'fa-solid fa-share-nodes',
  };

  access: HorizonAccess | null = null;
  overview: HorizonLeadOverview | null = null;
  /** The buyer's Horizon stage on this project, from the overview; `enquiry` until the backend says otherwise. */
  horizonStage: HorizonStage = 'enquiry';
  isStageBusy = false;
  insights: HorizonInsights | null = null;
  isInsightsLoading = false;
  hasInsightsError = false;
  /** This buyer's most viewed lots on the project, never every buyer's. */
  buyerMostViewed: HorizonLeadProperty[] = [];
  /** Recent property activity: this buyer's five most recent properties, or every page of them after View All. */
  properties: HorizonLeadProperty[] = [];
  propertiesHasMore = false;
  showAllProperties = false;
  isLoadingProperties = false;
  private propertiesPage = 1;
  /** The tab's date range as one object, replaced on every load so the heat map follows it. */
  dateRange: { from?: string; to?: string } = {};
  activity: HorizonActivityItem[] = [];
  activityHasMore = false;
  /** Request rows (by id) whose details are open in the timeline. */
  expandedRequests = new Set<string>();
  @ViewChild('activityTimeline') private activityTimeline?: ElementRef<HTMLElement>;
  private activityPage = 1;

  isLoading = false;
  isLoadingMoreActivity = false;
  isAccessBusy = false;
  hasLoadError = false;

  selectedDays = 30;
  selectedActivityFilter = 'all';
  /** The access action waiting for a second click, so suspend / cancel are never one-click. */
  pendingAccessAction: 'suspend' | 'cancel-invite' | null = null;
  /** The "⋯" menu's items, built when it opens so they follow the current access state. */
  accessMenuItems: MenuItem[] = [];
  private subscriptions: Subscription[] = [];
  private accessSubscription: Subscription;

  constructor(
    private horizon: HorizonTrackingService,
    private toaster: ToasterService,
    private router: Router
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['leadId'] || changes['projectId']) {
      this.loadAccess();
      this.load();
    }
  }

  ngOnDestroy(): void {
    this.clearSubscriptions();
    this.accessSubscription?.unsubscribe();
  }

  // ---- Loading ----------------------------------------------------------------

  private get range(): { from?: string; to?: string } {
    if (!this.selectedDays) {
      return {};
    }
    const brisbaneToday = moment().utcOffset(BRISBANE_UTC_OFFSET_MINUTES);
    return {
      from: brisbaneToday
        .clone()
        .subtract(this.selectedDays - 1, 'days')
        .format('YYYY-MM-DD'),
      to: brisbaneToday.format('YYYY-MM-DD'),
    };
  }

  load(): void {
    this.clearSubscriptions();
    if (!this.leadId) {
      this.overview = null;
      return;
    }
    this.isLoading = true;
    this.hasLoadError = false;
    this.activity = [];
    this.properties = [];
    this.insights = null;
    this.buyerMostViewed = [];
    this.activityPage = 1;
    this.dateRange = this.range;
    this.isInsightsLoading = true;
    this.hasInsightsError = false;

    this.subscriptions.push(
      this.horizon.getLeadOverview(this.leadId, this.range, this.projectId).subscribe({
        next: res => {
          this.overview = res?.data ?? null;
          this.horizonStage = this.overview?.horizon_stage ?? 'enquiry';
          this.isLoading = false;
        },
        error: () => {
          this.overview = null;
          this.hasLoadError = !shouldHideError(null);
          this.isLoading = false;
        },
      }),
      this.horizon.getLeadInsights(this.leadId, this.range, this.projectId).subscribe({
        next: res => {
          this.insights = res?.data ?? null;
          this.isInsightsLoading = false;
        },
        error: () => {
          this.hasInsightsError = !shouldHideError(null);
          this.isInsightsLoading = false;
        },
      })
    );
    this.loadActivity(true);
    this.loadProperties(true);
    this.loadMostViewed();
  }

  // ---- Recent property activity ---------------------------------------------------------------

  /** Property panels stay empty until Horizon tracks property browsing (`available: false` until then). */
  loadProperties(isFirstPage: boolean): void {
    if (isFirstPage) {
      this.propertiesPage = 1;
      this.properties = [];
    }
    this.isLoadingProperties = true;
    this.subscriptions.push(
      this.horizon.getLeadProperties(this.leadId, this.range, this.showAllProperties ? 10 : 5, this.propertiesPage, this.projectId).subscribe({
        next: res => {
          const data = res?.data;
          this.properties = data?.available ? [...this.properties, ...(data.items ?? [])] : [];
          // The preview shows the newest few; View All keeps paging.
          this.propertiesHasMore = this.showAllProperties ? !!data?.has_more : (data?.total ?? 0) > this.properties.length;
          this.isLoadingProperties = false;
        },
        error: () => {
          this.propertiesHasMore = false;
          this.isLoadingProperties = false;
        },
      })
    );
  }

  toggleAllProperties(): void {
    this.showAllProperties = !this.showAllProperties;
    this.loadProperties(true);
  }

  loadMoreProperties(): void {
    this.propertiesPage++;
    this.loadProperties(false);
  }

  /**
   * Most Viewed Properties: this buyer's own lot views on the project (the lead's properties endpoint),
   * ranked by views. The rows are re-sorted here in case the backend still returns them newest first.
   */
  private loadMostViewed(): void {
    this.subscriptions.push(
      this.horizon.getLeadMostViewed(this.leadId, this.range, MOST_VIEWED_SAMPLE, this.projectId).subscribe({
        next: res => {
          const items: HorizonLeadProperty[] = res?.data?.available ? (res.data.items ?? []) : [];
          this.buyerMostViewed = [...items].sort((a, b) => (b.views ?? 0) - (a.views ?? 0)).slice(0, MOST_VIEWED_COUNT);
        },
      })
    );
  }

  /** Reloads only the timeline — its type filter does not change the figures above it. */
  loadActivity(isFirstPage: boolean): void {
    if (!this.leadId) {
      return;
    }
    if (isFirstPage) {
      this.activityPage = 1;
      this.activity = [];
    }
    this.isLoadingMoreActivity = true;
    this.subscriptions.push(
      this.horizon.getLeadActivity(this.leadId, this.range, this.selectedActivityFilter, this.activityPage, 20, this.projectId).subscribe({
        next: res => {
          this.activity = [...this.activity, ...(res?.data?.items ?? []).map(toReadableActivity)];
          this.activityHasMore = !!res?.data?.has_more;
          this.isLoadingMoreActivity = false;
        },
        error: () => {
          this.activityHasMore = false;
          this.isLoadingMoreActivity = false;
        },
      })
    );
  }

  /** A summary tile was clicked: show the timeline filtered to that kind of request, scrolled into view. */
  showActivityFor(filter: string): void {
    if (this.selectedActivityFilter !== filter) {
      this.selectedActivityFilter = filter;
      this.loadActivity(true);
    }
    this.activityTimeline?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  isExpanded(item: HorizonActivityItem): boolean {
    return this.expandedRequests.has(item?.id);
  }

  toggleRequestDetail(item: HorizonActivityItem): void {
    if (!this.expandedRequests.delete(item?.id)) {
      this.expandedRequests.add(item?.id);
    }
  }

  /** Whether a request row has any detail worth expanding. */
  hasDetail(item: HorizonActivityItem): boolean {
    const detail = item?.detail;
    return !!detail && !!(detail.message || detail.topic || detail.preferred || detail.lot_name || detail.phone);
  }

  loadMoreActivity(): void {
    this.activityPage++;
    this.loadActivity(false);
  }

  /** The timeline is capped in height; the next page loads when the list is scrolled near its end. */
  onTimelineScroll(event: Event): void {
    const list = event.target as HTMLElement;
    const isNearEnd = list.scrollTop + list.clientHeight >= list.scrollHeight - 40;
    if (isNearEnd && this.activityHasMore && !this.isLoadingMoreActivity) {
      this.loadMoreActivity();
    }
  }

  private clearSubscriptions(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.subscriptions = [];
  }

  // ---- Access (invite / resend / suspend / restore / cancel) -------------------

  private loadAccess(): void {
    this.accessSubscription?.unsubscribe();
    this.access = null;
    this.pendingAccessAction = null;
    if (!this.leadId) {
      return;
    }
    this.accessSubscription = this.horizon.getLeadAccess(this.leadId).subscribe({
      next: res => (this.access = res?.data ?? null),
      error: () => (this.access = null),
    });
  }

  get inviteTooltip(): string {
    return this.access && !this.access.eligible
      ? (HORIZON_INELIGIBLE_REASONS[this.access.ineligible_reason] ?? 'This lead is not eligible for Horizon.')
      : '';
  }

  invite(): void {
    if (!this.access?.eligible || this.isAccessBusy) {
      return;
    }
    const isResend = this.access.status === 'invited';
    this.isAccessBusy = true;
    this.horizon.inviteLead(this.leadId).subscribe({
      next: res => {
        // QA: the toast reads "Invitation sent.", never the backend's generic "ok".
        this.toaster.Success(
          this.successMessage(res?.message, isResend ? 'Invitation sent. The previous link no longer works.' : 'Invitation sent.')
        );
        this.loadAccess();
        this.load();
        this.isAccessBusy = false;
      },
      error: err => {
        this.toaster.Error(this.errorMessage(err, 'The invitation could not be sent.'));
        this.isAccessBusy = false;
      },
    });
  }

  /** Suspend and cancel-invite ask for a second click; restore runs straight away. */
  requestAccessAction(action: 'suspend' | 'restore' | 'cancel-invite'): void {
    if (action === 'restore') {
      this.runAccessAction(action);
      return;
    }
    this.pendingAccessAction = action;
  }

  runAccessAction(action: 'suspend' | 'restore' | 'cancel-invite'): void {
    if (this.isAccessBusy) {
      return;
    }
    this.isAccessBusy = true;
    this.pendingAccessAction = null;
    this.horizon.changeLeadAccess(this.leadId, action).subscribe({
      next: res => {
        this.toaster.Success(this.successMessage(res?.message, ACCESS_ACTION_SUCCESS[action]));
        // Re-read the access so the banner reflects what the backend actually stored.
        this.loadAccess();
        this.load();
        this.isAccessBusy = false;
      },
      error: err => {
        this.toaster.Error(this.errorMessage(err, ACCESS_ACTION_FAILURE[action]));
        this.isAccessBusy = false;
      },
    });
  }

  /** Only a client with a Horizon account has a main project — the backend answers `422 not_invited` otherwise. */
  get canSetPrimaryProject(): boolean {
    return !!this.access && this.access.status !== 'not_invited';
  }

  get accessStatusLabel(): string {
    return ACCESS_STATUS_LABELS[this.access?.status] ?? '';
  }

  /** Shown once, on the access line: the latest Horizon activity, or the last sign-in when nothing is tracked yet. */
  get lastActiveAt(): string | null {
    return this.overview?.last_active_at ?? this.access?.last_login_at ?? null;
  }

  /** Suspend, cancel invite and main project live in the "⋯" menu; it is hidden when none apply. */
  get hasAccessMenu(): boolean {
    return this.access?.status === 'invited' || this.access?.status === 'active' || this.canSetPrimaryProject;
  }

  openAccessMenu(event: Event, menu: Menu): void {
    const items: MenuItem[] = [];
    if (this.canSetPrimaryProject) {
      items.push({
        label: this.access.is_primary_project ? 'Unset main project' : 'Make main project',
        icon: this.access.is_primary_project ? 'pi pi-star' : 'pi pi-star-fill',
        title: this.access.is_primary_project ? undefined : 'Horizon opens on this project when the client signs in',
        command: () => this.togglePrimaryProject(),
      });
    }
    if (this.access?.status === 'invited') {
      items.push({ label: 'Cancel invite', icon: 'pi pi-times', command: () => this.requestAccessAction('cancel-invite') });
    }
    if (this.access?.status === 'active') {
      items.push({
        label: 'Send access link',
        icon: 'pi pi-envelope',
        title: 'Email a single-use link to set a password and sign in',
        disabled: this.isAccessBusy,
        command: () => this.sendAccessLink(),
      });
      items.push({ label: 'Suspend access', icon: 'pi pi-ban', command: () => this.requestAccessAction('suspend') });
    }
    this.accessMenuItems = items;
    menu.toggle(event);
  }

  /** For an active buyer who cannot get in. An invited buyer needs Resend invite instead (`422 not_active`). */
  sendAccessLink(): void {
    if (this.access?.status !== 'active' || this.isAccessBusy) {
      return;
    }
    this.isAccessBusy = true;
    this.horizon.sendLeadAccessLink(this.leadId).subscribe({
      next: res => {
        this.toaster.Success(this.successMessage(res?.message, `Access link sent to ${this.access?.email ?? 'the buyer'}.`));
        this.isAccessBusy = false;
      },
      error: err => {
        this.toaster.Error(this.errorMessage(err, 'The access link could not be sent.'));
        this.isAccessBusy = false;
      },
    });
  }

  /**
   * Making this project the main one moves it off whichever project held it before, so Horizon then
   * opens here. Clearing it leaves Horizon on the project the customer enquired about first.
   */
  togglePrimaryProject(): void {
    if (!this.canSetPrimaryProject || this.isAccessBusy) {
      return;
    }
    const primary = !this.access.is_primary_project;
    this.isAccessBusy = true;
    this.horizon.setLeadPrimaryProject(this.leadId, primary).subscribe({
      next: res => {
        this.toaster.Success(this.successMessage(res?.message, primary ? 'This is now the main project in Horizon.' : 'Main project cleared.'));
        this.loadAccess();
        this.isAccessBusy = false;
      },
      error: err => {
        this.toaster.Error(this.errorMessage(err, 'The main project could not be changed.'));
        this.isAccessBusy = false;
      },
    });
  }

  /** The stage is per project, so it needs the lead's project and a client with a Horizon account. */
  get canSetHorizonStage(): boolean {
    return this.canSetPrimaryProject && !!this.projectId;
  }

  /** Saves the picked stage; on failure the select goes back to the stage the backend still has. */
  changeHorizonStage(stage: HorizonStage): void {
    const previous = this.overview?.horizon_stage ?? 'enquiry';
    if (!this.canSetHorizonStage || !stage || stage === previous) {
      return;
    }
    this.isStageBusy = true;
    this.horizon.setHorizonStage(this.leadId, this.projectId, stage).subscribe({
      next: res => {
        const label = HORIZON_STAGES.find(option => option.value === stage)?.label ?? stage;
        this.toaster.Success(this.successMessage(res?.message, `Horizon stage set to ${label}.`));
        if (this.overview) {
          this.overview = { ...this.overview, horizon_stage: stage };
        }
        this.isStageBusy = false;
      },
      error: err => {
        this.toaster.Error(this.errorMessage(err, 'The Horizon stage could not be changed.'));
        this.horizonStage = previous;
        this.isStageBusy = false;
      },
    });
  }

  /** The backend's own wording, unless it is a placeholder such as "ok" or "success". */
  private successMessage(message: string | undefined, fallback: string): string {
    const text = (message ?? '').trim();
    return text && !/^(ok|success|done|true)\.?$/i.test(text) ? text : fallback;
  }

  private errorMessage(err: any, fallback: string): string {
    return apiErrorBody(err).message || fallback;
  }

  // ---- View helpers -------------------------------------------------------------

  /** A metric the backend marks `available: false` is not tracked yet, so its tile is not drawn. */
  metric(key: string): HorizonSummaryMetric | null {
    const metric = this.overview?.summary?.[key] as HorizonSummaryMetric;
    return metric && metric.available !== false ? metric : null;
  }

  hasComparison(metric: HorizonSummaryMetric): boolean {
    return hasPeriodComparison(metric);
  }

  /** A tile's figure; "—" while the backend does not track that metric yet. */
  tileValue(card: { key: string; isDuration?: boolean }): string {
    const metric = this.metric(card.key);
    if (!metric) {
      return '—';
    }
    return card.isDuration ? this.formatDuration(metric.value) : (metric.value ?? 0).toLocaleString('en-AU');
  }

  /** Some rows come back without a name; the lot number keeps the row readable. */
  /**
   * The lot's name for Most Viewed and Recent Property Activity. A row can come back with neither `name` nor
   * `lot_name` (a lot the price list no longer has under that id); it reads "Lot not found" rather than going blank.
   */
  propertyName(row: HorizonLeadProperty): string {
    return row.name?.trim() || (row.lot_name ? `Lot ${row.lot_name}` : 'Lot not found');
  }

  /** The widest bar in the ranking is the most viewed lot; never zero so the bars always divide cleanly. */
  get mostViewedMax(): number {
    return Math.max(1, ...this.buyerMostViewed.map(property => property.views));
  }

  /** Sharing needs a buyer who can sign in to Horizon: invited or active. */
  get canShareWithBuyer(): boolean {
    return this.access?.status === 'invited' || this.access?.status === 'active';
  }

  /**
   * Opens the lot in Project → Price List (that page opens the lot named by `?id=`) in a new browser
   * tab, so the agent stays on this lead's Horizon tab.
   */
  openLot(projectId: string, unitId: string): void {
    if (projectId && unitId) {
      const url = this.router.serializeUrl(
        this.router.createUrlTree(['/projects', 'edit_', projectId, 'price-list'], { queryParams: { id: unitId } })
      );
      window.open(url, '_blank', 'noopener');
    }
  }

  /** The project's Reporting → Horizon Tracking tab. */
  viewProjectReport(): void {
    if (this.projectId) {
      this.router.navigate(['/projects', 'edit_', this.projectId, 'reporting'], { queryParams: { tab: 'horizon-tracking' } });
    }
  }

  formatMoney(value: number): string {
    return `$${(value ?? 0).toLocaleString('en-AU')}`;
  }

  get hasActivity(): boolean {
    return !!this.overview?.has_account && !!this.overview.last_active_at;
  }

  /** `icon` first, then the backend's normalised `kind` (contract §4.2). */
  activityIcon(item: HorizonActivityItem): string {
    return this.activityIcons[item?.icon] ?? ACTIVITY_KIND_ICONS[this.kindOf(item)] ?? 'fa-regular fa-circle-dot';
  }

  /** Information requests, messages, bookings and callbacks are what the agent has to act on, so they stand out. */
  isRequest(item: HorizonActivityItem): boolean {
    return ACTION_KINDS.includes(this.kindOf(item));
  }

  /** Colour group for the timeline icon. */
  activityTone(item: HorizonActivityItem): string {
    if (this.isRequest(item)) {
      return 'request';
    }
    return this.kindOf(item) === 'shortlist' || item?.icon === 'heart' ? 'shortlist' : 'default';
  }

  /** Rows from before `kind` existed fall back to `type`. */
  private kindOf(item: HorizonActivityItem): string {
    return item?.kind ?? item?.type ?? '';
  }

  formatDuration(totalSeconds: number): string {
    const hours = Math.floor((totalSeconds ?? 0) / 3600);
    const minutes = Math.floor(((totalSeconds ?? 0) % 3600) / 60);
    const seconds = Math.round((totalSeconds ?? 0) % 60);
    return hours > 0 ? `${hours}h ${minutes}m` : minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
  }

  formatActivityDate(date: string): string {
    return moment(date).utcOffset(BRISBANE_UTC_OFFSET_MINUTES).calendar(null, {
      sameDay: '[Today], h:mma',
      lastDay: '[Yesterday], h:mma',
      sameElse: 'D MMM, h:mma',
    });
  }

  formatShortDate(date: string): string {
    return moment(date).utcOffset(BRISBANE_UTC_OFFSET_MINUTES).format('D MMM YYYY');
  }

  trackByIndex(index: number): number {
    return index;
  }
}
